import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, vModelCheckbox as _vModelCheckbox } from "vue"

const _hoisted_1 = { class: "flex items-center justify-between mb-4" }
const _hoisted_2 = { class: "grid grid-cols-1 md:grid-cols-3 gap-4" }
const _hoisted_3 = ["disabled"]
const _hoisted_4 = ["value"]
const _hoisted_5 = { class: "flex flex-wrap gap-2" }
const _hoisted_6 = ["onUpdate:modelValue", "value"]
const _hoisted_7 = { class: "mr-1" }
const _hoisted_8 = ["disabled"]
const _hoisted_9 = ["value"]
const _hoisted_10 = { class: "mt-4 flex justify-end" }
const _hoisted_11 = ["disabled"]

import { ref, computed, watch, inject, onMounted } from 'vue'
import { useTheme } from '../../../utils/useTheme'
import { qualityGatesService } from '../../../services/quality-gates'
import type { QualityGatesFilters, PRStatus } from '../../../types/quality-gates'

interface Props {
  filters: QualityGatesFilters
}


export default /*@__PURE__*/_defineComponent({
  __name: 'QualityGatesFilters',
  props: {
    filters: {}
  },
  emits: ["update:filters", "apply-filters"],
  setup(__props: any, { emit: __emit }) {

const theme = inject('theme', useTheme())

const props = __props

const emit = __emit

// Local filters state
const localFilters = ref<QualityGatesFilters>({ ...props.filters })

// Dropdown options
const repositories = ref<string[]>([])
const gates = ref<string[]>([])
const loadingRepositories = ref(false)
const loadingGates = ref(false)

// Status options with styling
const statusOptions: { value: PRStatus; label: string; icon: string; activeClass: string }[] = [
  {
    value: 'pass',
    label: 'Pass',
    icon: '✓',
    activeClass:
      'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 border-green-500'
  },
  {
    value: 'fail',
    label: 'Fail',
    icon: '✗',
    activeClass: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 border-red-500'
  },
  {
    value: 'warning',
    label: 'Warning',
    icon: '⚠',
    activeClass:
      'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 border-yellow-500'
  }
]

// Check if there are changes
const hasChanges = computed(() => {
  return JSON.stringify(localFilters.value) !== JSON.stringify(props.filters)
})

// Watch for external filter changes
watch(
  () => props.filters,
  (newFilters) => {
    localFilters.value = { ...newFilters }
  },
  { deep: true }
)

// Format gate name for display
const formatGateName = (gateName: string): string => {
  return qualityGatesService.formatGateName(gateName)
}

// Fetch dropdown options
const loadDropdownOptions = async () => {
  // Fetch repositories and gates in parallel
  loadingRepositories.value = true
  loadingGates.value = true

  try {
    const [reposResult, gatesResult] = await Promise.all([
      qualityGatesService.fetchRepositories(),
      qualityGatesService.fetchGates()
    ])
    repositories.value = reposResult
    gates.value = gatesResult
  } finally {
    loadingRepositories.value = false
    loadingGates.value = false
  }
}

// Methods
const applyFilters = () => {
  emit('update:filters', { ...localFilters.value })
  emit('apply-filters', { ...localFilters.value })
}

const clearFilters = () => {
  localFilters.value = {
    repository: '',
    status: [],
    gate: ''
  }
  applyFilters()
}

// Load dropdown options on mount
onMounted(() => {
  loadDropdownOptions()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass([
      _unref(theme).isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
      'rounded-lg border p-4 shadow-sm'
    ])
  }, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("h3", {
        class: _normalizeClass([_unref(theme).isDark.value ? 'text-gray-200' : 'text-gray-900', 'text-lg font-medium'])
      }, " Filters ", 2),
      _createElementVNode("button", {
        class: _normalizeClass([
          _unref(theme).isDark.value
            ? 'text-gray-400 hover:text-gray-200'
            : 'text-gray-500 hover:text-gray-700',
          'text-sm font-medium'
        ]),
        onClick: clearFilters
      }, " Clear All ", 2)
    ]),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", null, [
        _createElementVNode("label", {
          for: "repository",
          class: _normalizeClass([
            _unref(theme).isDark.value ? 'text-gray-300' : 'text-gray-700',
            'block text-sm font-medium mb-1'
          ])
        }, " Repository ", 2),
        _withDirectives(_createElementVNode("select", {
          id: "repository",
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((localFilters.value.repository) = $event)),
          disabled: loadingRepositories.value,
          class: _normalizeClass([
            _unref(theme).isDark.value
              ? 'bg-gray-700 border-gray-600 text-gray-200'
              : 'bg-white border-gray-300 text-gray-700',
            'w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500',
            loadingRepositories.value ? 'opacity-50 cursor-wait' : ''
          ])
        }, [
          _cache[2] || (_cache[2] = _createElementVNode("option", { value: "" }, "All Repositories", -1)),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(repositories.value, (repo) => {
            return (_openBlock(), _createElementBlock("option", {
              key: repo,
              value: repo
            }, _toDisplayString(repo), 9, _hoisted_4))
          }), 128))
        ], 10, _hoisted_3), [
          [_vModelSelect, localFilters.value.repository]
        ])
      ]),
      _createElementVNode("div", null, [
        _createElementVNode("fieldset", null, [
          _createElementVNode("legend", {
            class: _normalizeClass([
              _unref(theme).isDark.value ? 'text-gray-300' : 'text-gray-700',
              'block text-sm font-medium mb-2'
            ])
          }, " Status ", 2),
          _createElementVNode("div", _hoisted_5, [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(statusOptions, (status) => {
              return _createElementVNode("label", {
                key: status.value,
                class: _normalizeClass([
                localFilters.value.status.includes(status.value)
                  ? status.activeClass
                  : _unref(theme).isDark.value
                    ? 'bg-gray-700 text-gray-300 border-gray-600'
                    : 'bg-gray-100 text-gray-600 border-gray-300',
                'flex items-center px-3 py-1.5 rounded-full text-sm cursor-pointer border transition-colors'
              ])
              }, [
                _withDirectives(_createElementVNode("input", {
                  "onUpdate:modelValue": ($event: any) => ((localFilters.value.status) = $event),
                  type: "checkbox",
                  value: status.value,
                  class: "sr-only"
                }, null, 8, _hoisted_6), [
                  [_vModelCheckbox, localFilters.value.status]
                ]),
                _createElementVNode("span", _hoisted_7, _toDisplayString(status.icon), 1),
                _createElementVNode("span", null, _toDisplayString(status.label), 1)
              ], 2)
            }), 64))
          ])
        ])
      ]),
      _createElementVNode("div", null, [
        _createElementVNode("label", {
          for: "gate",
          class: _normalizeClass([
            _unref(theme).isDark.value ? 'text-gray-300' : 'text-gray-700',
            'block text-sm font-medium mb-1'
          ])
        }, " Quality Gate ", 2),
        _withDirectives(_createElementVNode("select", {
          id: "gate",
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((localFilters.value.gate) = $event)),
          disabled: loadingGates.value,
          class: _normalizeClass([
            _unref(theme).isDark.value
              ? 'bg-gray-700 border-gray-600 text-gray-200'
              : 'bg-white border-gray-300 text-gray-700',
            'w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500',
            loadingGates.value ? 'opacity-50 cursor-wait' : ''
          ])
        }, [
          _cache[3] || (_cache[3] = _createElementVNode("option", { value: "" }, "All Gates", -1)),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(gates.value, (gate) => {
            return (_openBlock(), _createElementBlock("option", {
              key: gate,
              value: gate
            }, _toDisplayString(formatGateName(gate)), 9, _hoisted_9))
          }), 128))
        ], 10, _hoisted_8), [
          [_vModelSelect, localFilters.value.gate]
        ])
      ])
    ]),
    _createElementVNode("div", _hoisted_10, [
      _createElementVNode("button", {
        disabled: !hasChanges.value,
        class: _normalizeClass([
          hasChanges.value
            ? 'bg-blue-600 hover:bg-blue-700 text-white'
            : _unref(theme).isDark.value
              ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed',
          'px-4 py-2 rounded-md text-sm font-medium transition-colors'
        ]),
        onClick: applyFilters
      }, " Apply Filters ", 10, _hoisted_11)
    ])
  ], 2))
}
}

})